import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { getMovieById, allMovies, categories } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;

  const relatedMovies = movie
    ? allMovies
        .filter(m => m.id !== movie.id && (m.category === movie.category || m.tags.some(t => movie.tags.includes(t))))
        .slice(0, 5)
    : [];

  const categoryInfo = movie ? categories.find(c => c.slug === movie.category) : null;

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-400"></i>
          <p className="mt-4 text-foreground-500">内容不存在或已下架</p>
          <Link to="/" className="mt-3 inline-block text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.poster,
    dateCreated: `${movie.year}`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map(a => ({
      '@type': 'Person',
      name: a,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
      worstRating: '0',
    },
    genre: movie.tags,
    countryOfOrigin: {
      '@type': 'Country',
      name: movie.region,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${movie.title}可以在牛牛影视免费观看吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `可以！${movie.title}在牛牛影视全站免费在线观看，无需付费，无需会员，高清流畅不缓冲。`,
        },
      },
      {
        '@type': 'Question',
        name: `${movie.title}的画质怎么样？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${movie.title}在牛牛影视支持1080P超清画质，画面细腻流畅，为您带来影院级的观影体验。`,
        },
      },
      {
        '@type': 'Question',
        name: `牛牛影视有什么其他推荐？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `除了${movie.title}，牛牛影视还提供电影大全、电视剧全集、热门综艺和精品动漫，全站免费，每日更新。`,
        },
      },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${movie.title} - 免费在线观看 | 牛牛影视`}
        description={`${movie.title}，${movie.tags.join('、')}，${movie.year}年上映，评分${movie.rating}。${movie.description.substring(0, 100)}... 牛牛影视提供免费高清在线观看。`}
        keywords={`${movie.title},${movie.tags.join(',')},免费在线观看,牛牛影视,免费追剧`}
        canonicalUrl={`https://niuniu.tv/article/${movie.id}`}
        ogImage={movie.poster}
        ogType="article"
        schemaData={{ ...articleSchema, ...faqSchema }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: movie.categoryLabel, href: `/category/${movie.category}` },
                  { label: movie.title },
                ]}
              />

              <div className="mt-6 flex flex-col lg:flex-row gap-8">
                <div className="lg:w-[320px] flex-shrink-0">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                    <img
                      src={movie.poster}
                      alt={`${movie.title} - 免费在线观看`}
                      title={`${movie.title} - 牛牛影视免费高清在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                    {movie.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-md whitespace-nowrap">
                      <i className="ri-star-fill text-accent-500"></i>
                      {movie.rating}
                    </span>
                    <span className="text-sm text-foreground-500">{movie.year}</span>
                    <span className="text-foreground-400">·</span>
                    <span className="text-sm text-foreground-500">{movie.region}</span>
                    <span className="text-foreground-400">·</span>
                    <span className="text-sm text-foreground-500">{movie.categoryLabel}</span>
                    {movie.updateInfo && (
                      <>
                        <span className="text-foreground-400">·</span>
                        <span className="text-sm text-secondary-600 font-medium">{movie.updateInfo}</span>
                      </>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-md whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="bg-background-100 rounded-xl p-5 mb-5">
                    <h2 className="text-base font-semibold text-foreground-950 mb-2 font-heading">剧情简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-foreground-400">导演</span>
                      <span className="text-sm font-medium text-foreground-800">{movie.director}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-foreground-400">主演</span>
                      <span className="text-sm font-medium text-foreground-800">{movie.actors.join(' / ')}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-foreground-400">地区</span>
                      <span className="text-sm font-medium text-foreground-800">{movie.region}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-foreground-400">年份</span>
                      <span className="text-sm font-medium text-foreground-800">{movie.year}</span>
                    </div>
                  </div>

                  <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-fill text-xl"></i>
                    免费观看
                  </button>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="text-lg font-bold font-heading text-foreground-950 mb-3">
                  常见问题
                </h3>
                <div className="space-y-3">
                  {faqSchema.mainEntity.map((faq, idx) => (
                    <details key={idx} className="group bg-background-100 rounded-xl overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <span className="text-sm font-medium text-foreground-800">{faq.name}</span>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.acceptedAnswer.text}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </div>

              {relatedMovies.length > 0 && (
                <div className="mt-12">
                  <h2 className="text-lg font-bold font-heading text-foreground-950 mb-5">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-product-shop>
                    {relatedMovies.map((m) => (
                      <MovieCard key={m.id} movie={m} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}